import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Logo } from "../components/Logo";
import { CONTACT_EMAIL } from "../data/content";

const SECTIONS = [
  {
    id: "collect",
    title: "What information I collect",
    body: [
      "When you use the enquiry form on this website, I collect the details you choose to provide: your name, your email address, the service you are interested in, and your message describing what you need.",
      "The owner area of this website is protected by a login that uses essential security cookies. These are used only to keep the owner signed in — the public enquiry form does not set any cookies.",
      "This website is hosted on the Emergent platform, which may collect standard technical and analytics information (such as pages visited and general device or browser type) to operate, secure and improve the hosting service.",
    ],
  },
  {
    id: "why",
    title: "Why it is collected",
    body: [
      "Your details are collected for one purpose: so I can respond to your enquiry, prepare an accurate quote and, if you go ahead, deliver the agreed service.",
      "WA Digital Solutions aims to collect only the information necessary to respond to enquiries and deliver agreed services — nothing more.",
    ],
  },
  {
    id: "use",
    title: "How it is used",
    body: [
      "Your enquiry is stored in this website's secured database and a notification is emailed to me so I can reply to you personally, usually within one working day.",
      "Your information is never added to marketing lists, never sold, and never used for any purpose unrelated to your enquiry or the work you have asked me to do.",
    ],
  },
  {
    id: "protection",
    title: "How it is protected",
    body: [
      "This website is served over an encrypted (HTTPS) connection, and stored enquiries can only be accessed through my password-protected owner account.",
      "I aim to keep personal information only for as long as it is needed to handle your enquiry and any resulting work, and to handle it responsibly at all times.",
    ],
  },
  {
    id: "sharing",
    title: "Sharing with third parties",
    body: [
      "I do not share your personal or confidential information with third parties unless you have authorised it or I am legally required to do so.",
      "The platform providers that host this website and deliver its notification emails process information only as needed to operate those services.",
    ],
  },
  {
    id: "rights",
    title: "Your rights",
    body: [
      "Under the UK GDPR and the Data Protection Act 2018, you have the right to ask for a copy of the personal information I hold about you, to ask for it to be corrected or deleted, and to object to or restrict how it is used.",
      "If you are unhappy with how your information has been handled, you also have the right to raise a concern with the Information Commissioner's Office (ICO), the UK's data protection regulator.",
    ],
  },
  {
    id: "contact",
    title: "Privacy questions & requests",
    body: [
      `For any privacy-related question, or to make a request about your personal information (for example, to see, correct or delete it), simply email me at ${CONTACT_EMAIL}. I will respond personally.`,
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-canvas text-slate-100" data-testid="privacy-page">
      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#070B19]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="privacy-logo" aria-label="Back to homepage">
            <Logo compact />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
          >
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 md:py-20">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Legal & privacy</p>
        <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl" data-testid="privacy-title">
          Privacy & Confidentiality
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
          WA Digital Solutions is a small, independent IT support and digital solutions business based in Birmingham, UK.
          I take your privacy seriously: any personal information you share is handled respectfully, kept secure and used
          only for the purpose you provided it. Confidentiality is central to how I work — your business information,
          files and data stay private.
        </p>

        <div className="mt-8 rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.05] p-6" data-testid="privacy-qualification-note">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">
            <ShieldCheck size={14} />
            Professional approach to confidentiality
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            My understanding of privacy and confidentiality principles was developed in part through my TQUK Level 3
            Award in Coaching and Mentoring (RQF), which involved learning about the importance of confidentiality,
            professional boundaries and the responsible handling of information. This is relevant learning about
            confidentiality and professional conduct — it is not a specialist data-protection qualification, and I do
            not claim certified GDPR expertise.
          </p>
        </div>

        <nav className="mt-10 flex flex-wrap gap-2" aria-label="Page sections" data-testid="privacy-nav">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#privacy-${s.id}`}
              className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs text-slate-400 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
            >
              {s.title}
            </a>
          ))}
        </nav>

        <div className="mt-10 space-y-8">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={`privacy-${s.id}`}
              className="scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-7"
              data-testid={`privacy-section-${s.id}`}
            >
              <h2 className="font-display text-lg font-semibold text-slate-50">{s.title}</h2>
              {s.body.map((p, i) => (
                <p key={i} className="mt-3 text-sm leading-relaxed text-slate-400">
                  {p}
                </p>
              ))}
              {s.id === "contact" && (
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Privacy%20enquiry`}
                  data-testid="privacy-contact-button"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-5 py-2.5 text-sm font-semibold text-[#04101F] transition-transform duration-200 hover:scale-[1.03]"
                >
                  Email a privacy question
                </a>
              )}
            </section>
          ))}
        </div>

        <p className="mt-10 border-t border-white/5 pt-6 text-xs leading-relaxed text-slate-500" data-testid="privacy-footer-note">
          This notice reflects how this website currently operates — its enquiry form, owner login and platform hosting —
          and will be updated as the business develops. It is provided in good faith for a small independent business and
          is not legal advice. Last updated: October 2026.
        </p>
      </main>
    </div>
  );
}
